import {
  AuthMethod,
  CredentialType,
  type UserCredential,
  type UserDetail,
} from '@/portalApi'
import useAuthStore from '@/stores/auth'
import { portalApi } from '@/util/apiClient'
import { queryClient } from '@/util/queryClient'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef, type Ref } from 'vue'

type AccountSecurity = {
  credentials: Ref<UserCredential[] | undefined>
  loadingCredentials: Ref<boolean>
  credentialsFailed: Ref<boolean>
  account: Ref<UserDetail | undefined>
  loadingAccount: Ref<boolean>
  /** Signs in through the organization's identity provider, which owns MFA too. */
  isIdpManaged: ComputedRef<boolean>
  /**
   * Holds an authenticator app or security key with no backup codes behind it, which
   * locks the user out if the device is lost.
   */
  missingBackupCodes: ComputedRef<boolean>
}

/**
 * The signed-in user's credentials and how they sign in, as the account page shows them
 * and the user menu flags them. Both read through the same query keys, so they share one
 * fetch.
 */
const useAccountSecurity = (): AccountSecurity => {
  const authStore = useAuthStore()
  const userId = computed(() => authStore.currentUser?.id)

  const {
    data: credentials,
    isPending: loadingCredentials,
    isError: credentialsFailed,
  } = useQuery(
    {
      queryKey: computed(() => ['userCredentials', userId.value]),
      queryFn: () =>
        portalApi.usersIdCredentialsGet({ path: { id: userId.value ?? '' } }),
      enabled: computed(() => !!userId.value),
    },
    queryClient,
  )
  // The list behind `currentUser` leaves out how each user signs in; only the detail read has it.
  const { data: account, isPending: loadingAccount } = useQuery(
    {
      queryKey: computed(() => ['accountDetail', userId.value]),
      queryFn: () => portalApi.usersIdGet({ path: { id: userId.value ?? '' } }),
      enabled: computed(() => !!userId.value),
    },
    queryClient,
  )

  const isIdpManaged = computed(
    () => account.value?.auth_method === AuthMethod.idp,
  )

  // Check if a user has an MFA method configured but no backup codes.
  const missingBackupCodes = computed(() => {
    const list = credentials.value
    if (!list || isIdpManaged.value) return false
    const hasType = (type: CredentialType) => list.some((c) => c.type === type)
    return (
      (hasType(CredentialType.otp) || hasType(CredentialType.webauthn)) &&
      !hasType(CredentialType.recovery_codes)
    )
  })

  return {
    credentials,
    loadingCredentials,
    credentialsFailed,
    account,
    loadingAccount,
    isIdpManaged,
    missingBackupCodes,
  }
}

export default useAccountSecurity
